import React, { useEffect, useReducer, useRef, useState } from "react";

import { Input, InputChangeEvent, NumericTextBox, NumericTextBoxChangeEvent, SwitchChangeEvent } from "@progress/kendo-react-inputs";
import { getInitialState } from "@selas/state-management";
import { EntityEditor, handleChange, IAddEntityScreenProps, IEditEntityScreenProps, YesNoSwitch } from "@selas/ui-components";
import { isNullOrEmpty } from "@selas/utils";
import { useTranslation } from "react-i18next";

import Endpoint from "../../../services/api/endpoint";
import opportunityOriginReducer from "../../../state/reducers/opportunityOriginReducer";
import { IOrigin, IProjectGroup } from "../../../utils/types/models";

const OpportunityOriginEditor: React.FC<IAddEntityScreenProps<IProjectGroup> | IEditEntityScreenProps<IProjectGroup>> = (
	props: IAddEntityScreenProps<IProjectGroup> | IEditEntityScreenProps<IProjectGroup>
) => {
	const { t } = useTranslation();

	const [state, dispatch] = useReducer(opportunityOriginReducer, getInitialState<IOrigin>());
	const initialOrigin: IOrigin = {
		id: (props as IEditEntityScreenProps<IOrigin>).recordId || 0,
		name: "",
		active: true,
		chanceOfSucces: 0
	};
	const [opportunityOrigin, setOpportunityOrigin] = useState<IOrigin>(initialOrigin);
	const [dataChanged, setDataChanged] = useState<boolean>(false);
	const firstField: React.MutableRefObject<Input> = useRef();

	useEffect(() => {
		if (state.entity) {
			setOpportunityOrigin(state.entity);
		}
	}, [state.entity]);

	function onChange(event: InputChangeEvent | SwitchChangeEvent | NumericTextBoxChangeEvent): void {
		setOpportunityOrigin(handleChange(opportunityOrigin, event));
		setDataChanged(true);
	}

	function getErrorMessages(): string[] {
		const messages: string[] = [];
		if (isNullOrEmpty(opportunityOrigin.name)) {
			messages.push(t("fill_in_required_field", { field: t("name").toLowerCase() }));
		}
		return messages;
	}

	function onActionButtonClicked(close: boolean, record: IOrigin): void {
		if (record && !close) {
			setOpportunityOrigin(record);
			setDataChanged(false);
		}
		props.actionButtonClicked(close, record);
	}

	const readonly: boolean = (props as IEditEntityScreenProps<IOrigin>).readonly;

	return (
		<EntityEditor
			width="70%"
			record={opportunityOrigin}
			endpoint={Endpoint.Origins}
			entityState={state}
			entityType={t("origin")}
			dispatch={dispatch}
			dataChanged={dataChanged}
			readonly={readonly}
			recordName={opportunityOrigin.name}
			actionButtonClicked={onActionButtonClicked}
			getErrorMessages={getErrorMessages}
			firstFieldRef={firstField}
		>
			<div className="k-form">
				<label className="k-form-field">
					<span>{t("name")} *</span>
					<Input name="name" ref={firstField} value={opportunityOrigin.name} onChange={onChange} required disabled={readonly} />
				</label>
				<label className="k-form-field">
					<span>{t("chanceOfSucces")} *</span>
					<NumericTextBox
						name="chanceOfSucces"
						value={opportunityOrigin.chanceOfSucces}
						onChange={onChange}
						required
						disabled={readonly}
						format="p"
						defaultValue={0}
						min={0}
						max={1}
						step={0.1}
					/>
				</label>
				{!props.hideActive && (
					<div className="k-form-field">
						<div>{t("active")}</div>
						<YesNoSwitch name="active" checked={opportunityOrigin.active} onChange={onChange} disabled={readonly} />
					</div>
				)}
			</div>
		</EntityEditor>
	);
};

export default OpportunityOriginEditor;
